import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { toast } from "sonner";
import { 
  Mail, 
  CreditCard, 
  Share2, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Rocket,
  Settings,
  Shield,
  Save,
  RefreshCw
} from "lucide-react";
import { API } from "../../apiConfig";


const StatusBadge = ({ configured }) => {
  if (configured) {
    return <Badge className="bg-green-500/10 text-green-600 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>;
  }
  return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" /> Not Configured</Badge>;
};

const QuickStartGuide = ({ token }) => {
  const [showPasswords, setShowPasswords] = useState({});
  const [loading, setLoading] = useState({});
  const [configStatus, setConfigStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  
  // Form states
  const [emailConfig, setEmailConfig] = useState({
    email: "mark@arawol.art",
    appPassword: ""
  });
  
  const [paypalConfig, setPaypalConfig] = useState({
    clientId: "",
    clientSecret: "",
    sandbox: true
  });
  
  const [stripeConfig, setStripeConfig] = useState({
    publishableKey: "",
    secretKey: "",
    testMode: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConfigStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/settings/status`, { headers });
      setConfigStatus(res.data);
      
      // Pre-fill email if configured
      if (res.data.email?.email) {
        setEmailConfig(prev => ({ ...prev, email: res.data.email.email }));
      }
      // Pre-fill PayPal sandbox status
      if (res.data.paypal?.configured) {
        setPaypalConfig(prev => ({ ...prev, sandbox: res.data.paypal.sandbox }));
      }
    } catch (error) {
      console.error("Error fetching config status:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchConfigStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const togglePassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const saveEmailConfig = async () => {
    if (!emailConfig.appPassword) {
      toast.error("Please enter your Outlook App Password");
      return;
    }
    
    setLoading(prev => ({ ...prev, emailSave: true }));
    try {
      const res = await axios.post(`${API}/settings/email`, {
        email: emailConfig.email,
        app_password: emailConfig.appPassword
      }, { headers });
      
      if (res.data.success) {
        toast.success("Email configuration saved!");
        fetchConfigStatus();
        if (res.data.restart_required) {
          toast.info("Restart backend for changes to take effect", { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save email config");
    } finally {
      setLoading(prev => ({ ...prev, emailSave: false }));
    }
  };

  const testEmailConfig = async () => {
    setLoading(prev => ({ ...prev, emailTest: true }));
    try {
      const res = await axios.post(`${API}/settings/test/email`, {
        recipient: emailConfig.email
      }, { headers });
      
      if (res.data.success) {
        toast.success("Test email sent successfully!");
        fetchConfigStatus();
      } else {
        toast.error(res.data.error || "Email test failed");
      }
    } catch (error) {
      toast.error("Email test failed - check your credentials");
    } finally {
      setLoading(prev => ({ ...prev, emailTest: false }));
    }
  };

  const savePayPalConfig = async () => {
    if (!paypalConfig.clientId || !paypalConfig.clientSecret) {
      toast.error("Please enter both Client ID and Client Secret");
      return;
    }
    
    setLoading(prev => ({ ...prev, paypalSave: true }));
    try {
      const res = await axios.post(`${API}/settings/paypal`, {
        client_id: paypalConfig.clientId,
        client_secret: paypalConfig.clientSecret,
        sandbox: paypalConfig.sandbox
      }, { headers });
      
      if (res.data.success) {
        toast.success("PayPal configuration saved!");
        fetchConfigStatus();
        if (res.data.restart_required) {
          toast.info("Restart backend for changes to take effect", { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save PayPal config");
    } finally {
      setLoading(prev => ({ ...prev, paypalSave: false }));
    }
  };

  const testPayPalConfig = async () => {
    setLoading(prev => ({ ...prev, paypalTest: true }));
    try {
      const res = await axios.post(`${API}/settings/test/paypal`, {}, { headers });
      
      if (res.data.success) {
        toast.success("PayPal connection successful!");
        fetchConfigStatus();
      } else {
        toast.error(res.data.error || "PayPal test failed");
      }
    } catch (error) {
      toast.error("PayPal test failed - check your credentials");
    } finally {
      setLoading(prev => ({ ...prev, paypalTest: false }));
    }
  };

  const saveStripeConfig = async () => {
    if (!stripeConfig.secretKey) {
      toast.error("Please enter your Stripe Secret Key");
      return;
    }
    
    setLoading(prev => ({ ...prev, stripeSave: true }));
    try {
      const res = await axios.post(`${API}/settings/stripe`, {
        publishable_key: stripeConfig.publishableKey,
        secret_key: stripeConfig.secretKey,
        test_mode: stripeConfig.testMode
      }, { headers });
      
      if (res.data.success) {
        toast.success("Stripe configuration saved!");
        fetchConfigStatus();
        if (res.data.restart_required) {
          toast.info("Restart backend for changes to take effect", { duration: 5000 });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Stripe config");
    } finally {
      setLoading(prev => ({ ...prev, stripeSave: false }));
    }
  };

  const testStripeConfig = async () => {
    setLoading(prev => ({ ...prev, stripeTest: true }));
    try {
      const res = await axios.post(`${API}/settings/test/stripe`, {}, { headers });
      
      if (res.data.success) {
        toast.success("Stripe connection successful!");
        fetchConfigStatus();
      } else {
        toast.error(res.data.error || "Stripe test failed");
      }
    } catch (error) {
      toast.error("Stripe test failed - check your credentials");
    } finally {
      setLoading(prev => ({ ...prev, stripeTest: false }));
    }
  };

  const completedCount = configStatus 
    ? [configStatus.email?.configured, configStatus.stripe?.configured, configStatus.paypal?.configured, configStatus.social?.configured].filter(Boolean).length 
    : 0;

  return (
    <div className="space-y-6" data-testid="quick-start-guide">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20">
          <Rocket className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold">Quick Start Guide</h2>
          <p className="text-muted-foreground">Set up your integrations to unlock all features</p>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Setup Progress</h3>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white">{completedCount} of 4 complete</Badge>
              <Button variant="ghost" size="sm" onClick={fetchConfigStatus} disabled={loadingStatus}>
                <RefreshCw className={`w-4 h-4 ${loadingStatus ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
              <Mail className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium">Email</p>
                <StatusBadge configured={configStatus?.email?.configured} />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
              <CreditCard className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-sm font-medium">Stripe</p>
                <StatusBadge configured={configStatus?.stripe?.configured} />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
              <CreditCard className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-sm font-medium">PayPal</p>
                <StatusBadge configured={configStatus?.paypal?.configured} />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
              <Share2 className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-sm font-medium">Social Media</p>
                <StatusBadge configured={configStatus?.social?.configured} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Tabs */}
      <Tabs defaultValue="email" className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" /> Email
          </TabsTrigger>
          <TabsTrigger value="stripe" className="gap-2">
            <CreditCard className="w-4 h-4" /> Stripe
          </TabsTrigger>
          <TabsTrigger value="paypal" className="gap-2">
            <CreditCard className="w-4 h-4" /> PayPal
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2">
            <Share2 className="w-4 h-4" /> Social Media
          </TabsTrigger>
        </TabsList>

        {/* Email Setup */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-500" />
                    Email Notifications Setup
                  </CardTitle>
                  <CardDescription>
                    Configure Outlook/Microsoft 365 to send order confirmations, shipping updates, and wholesale approvals
                  </CardDescription>
                </div>
                <StatusBadge configured={configStatus?.email?.configured} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step-by-step guide */}
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="step1">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">1</span>
                      Enable 2FA on your Microsoft Account
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <p className="text-muted-foreground">
                      App Passwords require two-factor authentication to be enabled on your Microsoft account.
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Microsoft Security Settings <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Click &quot;Advanced security options&quot;</li>
                      <li>Enable &quot;Two-step verification&quot; if not already enabled</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step2">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">2</span>
                      Generate an App Password
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Microsoft Security <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Scroll to &quot;App passwords&quot; section</li>
                      <li>Click &quot;Create a new app password&quot;</li>
                      <li>Name it &quot;Art By Arawol&quot; and click &quot;Create&quot;</li>
                      <li>Copy the generated password (you&apos;ll only see it once!)</li>
                    </ol>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                      <p className="font-medium text-amber-700">⚠️ Important:</p>
                      <p className="text-amber-600">The app password will only be shown once. Copy it immediately!</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Configuration Form */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Enter Your Credentials
                </h4>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input 
                      value={emailConfig.email}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="mark@arawol.art"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>App Password</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input 
                          type={showPasswords.email ? "text" : "password"}
                          value={emailConfig.appPassword}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, appPassword: e.target.value }))}
                          placeholder="Enter your Outlook app password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => togglePassword("email")}
                        >
                          {showPasswords.email ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={saveEmailConfig}
                      disabled={loading.emailSave}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {loading.emailSave ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Configuration</>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={testEmailConfig}
                      disabled={loading.emailTest || !configStatus?.email?.configured}
                    >
                      {loading.emailTest ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                      ) : (
                        "Send Test Email"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stripe Setup */}
        <TabsContent value="stripe">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-500" />
                    Stripe Payments Setup
                  </CardTitle>
                  <CardDescription>
                    Accept credit/debit card payments securely via Stripe
                  </CardDescription>
                </div>
                <StatusBadge configured={configStatus?.stripe?.configured} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="step1">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-sm font-medium">1</span>
                      Create a Stripe Account
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline inline-flex items-center gap-1">Stripe Sign Up <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Complete the registration with your business details</li>
                      <li>Verify your email address</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step2">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-sm font-medium">2</span>
                      Get Your API Keys
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline inline-flex items-center gap-1">Stripe API Keys <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Copy your <strong>Publishable key</strong> (starts with pk_)</li>
                      <li>Copy your <strong>Secret key</strong> (starts with sk_)</li>
                    </ol>
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                      <p className="font-medium text-purple-700">💡 Test vs Live Mode:</p>
                      <p className="text-purple-600">Start with Test keys (pk_test_, sk_test_) for testing. Switch to Live keys when ready for real payments.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Configuration Form */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Enter Your Stripe Credentials
                </h4>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Publishable Key</Label>
                    <Input 
                      value={stripeConfig.publishableKey}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, publishableKey: e.target.value }))}
                      placeholder="pk_test_... or pk_live_..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secret Key</Label>
                    <div className="relative">
                      <Input 
                        type={showPasswords.stripe ? "text" : "password"}
                        value={stripeConfig.secretKey}
                        onChange={(e) => setStripeConfig(prev => ({ ...prev, secretKey: e.target.value }))}
                        placeholder="sk_test_... or sk_live_..."
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => togglePassword("stripe")}
                      >
                        {showPasswords.stripe ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="stripeTestMode"
                      checked={stripeConfig.testMode}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, testMode: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="stripeTestMode" className="text-sm cursor-pointer">
                      Test Mode (use test keys for development)
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={saveStripeConfig}
                      disabled={loading.stripeSave}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {loading.stripeSave ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Configuration</>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={testStripeConfig}
                      disabled={loading.stripeTest || !configStatus?.stripe?.configured}
                    >
                      {loading.stripeTest ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                      ) : (
                        "Test Connection"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PayPal Setup */}
        <TabsContent value="paypal">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-yellow-500" />
                    PayPal Payments Setup
                  </CardTitle>
                  <CardDescription>
                    Enable PayPal as a payment option for your customers
                  </CardDescription>
                </div>
                <StatusBadge configured={configStatus?.paypal?.configured} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="step1">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 text-sm font-medium">1</span>
                      Create a PayPal Developer Account
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">PayPal Developer <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Log in with your PayPal business account</li>
                      <li>If you don&apos;t have one, create a PayPal Business account first</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step2">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 text-sm font-medium">2</span>
                      Create a REST API App
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">My Apps & Credentials <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Click &quot;Create App&quot;</li>
                      <li>Name it &quot;Art By Arawol&quot;</li>
                      <li>Select &quot;Merchant&quot; as the app type</li>
                      <li>Click &quot;Create App&quot;</li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step3">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 text-sm font-medium">3</span>
                      Get Your API Credentials
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>After creating the app, you&apos;ll see your Client ID</li>
                      <li>Click &quot;Show&quot; under Secret to reveal your Client Secret</li>
                      <li>Start with Sandbox credentials for testing</li>
                      <li>Switch to Live credentials when ready for production</li>
                    </ol>
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <p className="font-medium text-green-700">💡 Tip:</p>
                      <p className="text-green-600">Test with Sandbox first! Use the sandbox test accounts to make test purchases without real money.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Configuration Form */}
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Enter Your Credentials
                </h4>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input 
                      value={paypalConfig.clientId}
                      onChange={(e) => setPaypalConfig(prev => ({ ...prev, clientId: e.target.value }))}
                      placeholder="Enter your PayPal Client ID"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret</Label>
                    <div className="relative">
                      <Input 
                        type={showPasswords.paypal ? "text" : "password"}
                        value={paypalConfig.clientSecret}
                        onChange={(e) => setPaypalConfig(prev => ({ ...prev, clientSecret: e.target.value }))}
                        placeholder="Enter your PayPal Client Secret"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => togglePassword("paypal")}
                      >
                        {showPasswords.paypal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch 
                      checked={paypalConfig.sandbox}
                      onCheckedChange={(checked) => setPaypalConfig(prev => ({ ...prev, sandbox: checked }))}
                    />
                    <Label className="font-normal">
                      Sandbox Mode {paypalConfig.sandbox ? "(Testing)" : "(Production)"}
                    </Label>
                  </div>
                  {!paypalConfig.sandbox && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                      <p className="font-medium text-amber-700">⚠️ Production Mode</p>
                      <p className="text-amber-600">Real payments will be processed. Make sure you&apos;re using live credentials.</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      onClick={savePayPalConfig}
                      disabled={loading.paypalSave}
                      className="bg-yellow-600 hover:bg-yellow-700"
                    >
                      {loading.paypalSave ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Save Configuration</>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={testPayPalConfig}
                      disabled={loading.paypalTest || !configStatus?.paypal?.configured}
                    >
                      {loading.paypalTest ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                      ) : (
                        "Test Connection"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Media Setup */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-purple-500" />
                    Social Media Publishing Setup
                  </CardTitle>
                  <CardDescription>
                    Connect your social accounts to publish directly from the Marketing tab
                  </CardDescription>
                </div>
                <StatusBadge configured={configStatus?.social?.configured} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>Quick Access:</strong> You can configure social media credentials directly in the{" "}
                  <span className="font-medium">Marketing tab → Settings view</span>
                </p>
              </div>

              {configStatus?.social?.platforms?.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 font-medium mb-2">Connected Platforms:</p>
                  <div className="flex gap-2">
                    {configStatus.social.platforms.map(platform => (
                      <Badge key={platform} className="bg-green-100 text-green-700 capitalize">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Accordion type="single" collapsible className="w-full">
                {/* Twitter */}
                <AccordionItem value="twitter">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                        <span className="text-white font-bold text-sm">𝕏</span>
                      </div>
                      Twitter / X
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-10 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://developer.twitter.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Twitter Developer Portal <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Create a new Project and App</li>
                      <li>Enable OAuth 1.0a with Read and Write permissions</li>
                      <li>Generate API Key, API Secret, Access Token, and Access Token Secret</li>
                    </ol>
                    <div className="text-xs text-muted-foreground">
                      <strong>Required:</strong> API Key, API Secret, Access Token, Access Token Secret
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Facebook */}
                <AccordionItem value="facebook">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                        <span className="text-white font-bold">f</span>
                      </div>
                      Facebook
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-10 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Facebook Developers <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Create a new app (Business type)</li>
                      <li>Add Facebook Login product</li>
                      <li>Get Page Access Token for your Facebook Page</li>
                    </ol>
                    <div className="text-xs text-muted-foreground">
                      <strong>Required:</strong> App ID, App Secret, Page ID, Page Access Token
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Instagram */}
                <AccordionItem value="instagram">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">IG</span>
                      </div>
                      Instagram
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-10 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Convert to Instagram Business or Creator account</li>
                      <li>Connect Instagram to a Facebook Page</li>
                      <li>Use the same Facebook App from above</li>
                      <li>Add Instagram Graph API product</li>
                      <li>Get Instagram Business Account ID</li>
                    </ol>
                    <div className="text-xs text-muted-foreground">
                      <strong>Required:</strong> App ID, App Secret, Instagram Business Account ID, Access Token
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* LinkedIn */}
                <AccordionItem value="linkedin">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-700 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">in</span>
                      </div>
                      LinkedIn
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-10 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://www.linkedin.com/developers" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">LinkedIn Developers <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Create a new app</li>
                      <li>Request &quot;Share on LinkedIn&quot; and &quot;Sign In with LinkedIn&quot; products</li>
                      <li>Complete verification for your company page</li>
                    </ol>
                    <div className="text-xs text-muted-foreground">
                      <strong>Required:</strong> Client ID, Client Secret, Organization ID (optional)
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Pinterest */}
                <AccordionItem value="pinterest">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">P</span>
                      </div>
                      Pinterest
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-10 space-y-3">
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                      <li>Go to <a href="https://developers.pinterest.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Pinterest Developers <ExternalLink className="w-3 h-3" /></a></li>
                      <li>Create a new app</li>
                      <li>Request access to the Pins API</li>
                      <li>Generate an Access Token</li>
                    </ol>
                    <div className="text-xs text-muted-foreground">
                      <strong>Required:</strong> App ID, App Secret, Access Token
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Help Section */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium">Configuration Saved Automatically</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Your credentials are saved securely. Payment settings (Stripe, PayPal) take effect immediately — no restart needed. Email settings may require a backend restart.
              </p>
              <div className="mt-2 bg-slate-900 text-slate-100 p-3 rounded font-mono text-sm flex items-center justify-between">
                <span>sudo supervisorctl restart backend</span>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard("sudo supervisorctl restart backend")} className="text-slate-400 hover:text-white">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickStartGuide;
